<template>
  <div class="page-root">
    <ObsExampleDialog></ObsExampleDialog>

    <!-- 플랫폼 연결 타이틀/설명 — 섹션 카드 밖 -->
    <h1>
      <span class="font-weight-bold" style="font-family: maplestory"> {{ $t("broadcastManage.title") }} </span>
    </h1>
    <p class="text-body-2 grey--text mt-1 mb-3">{{ $t("broadcastManage.subtitle") }}</p>
    <!-- 플랫폼 연동(치지직/트위치/SOOP/유튜브) — 방송하기와 동일한 섹션 카드(단, box-shadow 없음) -->
    <div
      class="app-page__content platform-section mt-4 pa-4"
      style="border-radius: 10px; background-color: var(--v-surface-base); box-shadow: none"
    >
      <BroadcastManagePage embedded></BroadcastManagePage>
    </div>

    <h1 class="d-flex align-center mt-6">
      <span class="font-weight-bold" style="font-family: maplestory"> 방송하기 </span>
      <!-- 로컬에서만: 현재 선택 pet_id -->
      <span v-if="isLocalEnv && petId" class="ml-4" style="font-size: 13px; font-weight: 400; color: #9e9e9e">
        {{ petId }}
      </span>
    </h1>
    <!-- 래퍼 통합. elevation-1은 금지: box-shadow !important라 .active 글로우를 덮음 (기본 그림자는 전역 .app-page__content가 제공) -->
    <div
      class="app-page__content layout-2-1 mt-4 pa-4"
      :class="{ active: 0 !== liveModelCount }"
      style="border-radius: 10px; background-color: var(--v-surface-base); z-index: 9"
    >
      <div class="d-flex justify-space-between align-center">
        <ObsLinkDialog
          ref="obsLinkDialog"
          :email="email"
          :streamerAliasId="context.streamerAliasId"
          :liveModelCount="`${onAirCount}`"
          :breakdown="liveModelBreakdown"
        ></ObsLinkDialog>

        <div class="d-flex justify-end">
          <v-sync-trigger ref="requestSyncIcon"></v-sync-trigger>
          <AppSwitch v-model="isEnableLiveModel" label-left class="align-self-center"
            >설정 실시간으로 업데이트 하기</AppSwitch
          >
        </div>
      </div>

      <div class="mt-2"></div>
      <div class="d-flex" style="gap: 10px">
        <!-- style="width: 200px; height: 200px" -->
        <TheLeftController
          class="flex-shrink-0 align-self-start"
          :pet-id="petId"
          @change="updateOuterVisibility"
        ></TheLeftController>
        <!-- 미리보기 카드: TheLeftController와 동일한 카드 효과(pa-4 elevation-1 + radius 4px) -->
        <div class="flex-shrink-0 align-self-start pa-4 elevation-1" style="border-radius: 4px">
          <div class="pos-rel w-100 h-100" style="width: 200px; height: 200px; border-radius: 10px; overflow: hidden">
            <!-- 미리보기: 토큰으로 virtupet sub → 자기 pub fan-out을 받아 미러(오버레이와 동일 경로) -->
            <BroadcastBox
              v-if="broadcastToken"
              ref="broadcastBox"
              :sources="['virtupet']"
              :token="broadcastToken"
              class="scale-05"
              with-outer
              :size="400"
            ></BroadcastBox>
            <div class="pos-abs" style="right: 40px; bottom: 40px">
              <v-sync-trigger ref="modelSyncTrigger"></v-sync-trigger>
            </div>
          </div>
        </div>
        <!-- 캐릭터 오른쪽에 현재상태 배치  -->
        <!-- 탭 : 스트리머설정, 룰설정, 위젯 -->
        <!-- style="width: 200px; height: 200px" -->
        <TheRightController :pet-id="petId"></TheRightController>
      </div>
    </div>
    <!-- (테스트 채팅 위젯은 TestChatWidget으로 추출됨 — 필요한 페이지에 <TestChatWidget/>) -->
  </div>
</template>

<script>
import ObsLinkDialog from "./components/ObsLinkDialog.vue";
import delay from "@/utils/delay";

import ObsExampleDialog from "./components/ObsExampleDialog.vue";
import BroadcastManagePage from "@/views/BroadcastManage/BroadcastManagePage.vue";
import SupabaseApi from "@/api/SupabaseApi";
import { supabase } from "@/utils/supabase";

// components
import VSyncTrigger from "@/components/atomic/moleculres/VSyncTrigger.vue";

import TheLeftController from "./fragments/TheLeftController.vue";
import TheRightController from "./fragments/TheRightController.vue";

import { getVirtupetPubSocket } from "@/domain/stream/pub/VirtupetPubSocket";
import { isLocal } from "@/utils/env-utils";

// 제어판 pub 연결은 펫 단위 VirtupetPubSocket(getVirtupetPubSocket)로 통합됨.

// TODO:
// 주기적으로 체크해야 함...
// 리모컨이 통신중인지 아닌지
// destoryed에 disconnecdt

export default {
  components: {
    TheLeftController,
    TheRightController,
    ObsLinkDialog,
    ObsExampleDialog,
    BroadcastManagePage,
    VSyncTrigger,
  },

  async created() {
    // 표시/가드용 email은 세션에서 (구 localStorage userEmail 레거시 제거)
    const {
      data: { session },
    } = await supabase.auth.getSession();
    this.email = (session && session.user && session.user.email) || null;

    // 미리보기(BroadcastBox)는 토큰으로 virtupet을 sub해서 스스로 모델/인스트럭션/레벨을 로드하고
    // 자기 pub fan-out을 받아 반응한다(미러). 여기선 토큰만 확보해 prop으로 넘긴다.
    this.broadcastToken = await this.$store.dispatch("broadcast/ensureToken");

    // 리액션 목록(reaction 테이블: system + 내것) — 하단 제어판/셀렉터 store용
    const reactions = await SupabaseApi.Reaction.fetchAll();
    this.$store.commit("__setReactions", SupabaseApi.Reaction.toMaps(reactions));

    // 표시명 = user_account.name (구 streamer_alias_id). OBS 링크 다이얼로그/식별 라벨용.
    const myProfile = await SupabaseApi.fetchMyProfile();
    this.context.streamerAliasId = (myProfile && myProfile.name) || "";

    this.context.virtupetProfile = (await SupabaseApi.PetProfile.fetchProfile()) || {};
  },
  async mounted() {
    SupabaseApi.Metric.trackClick("STUDIO_LIVE_REMOTE_VISIT"); // 분석용 기록 (fire-and-forget, 구 Slack 알림 대체)

    await delay(300);
    await this.initLiveRemote();
  },
  data() {
    return {
      isEnableLiveModel: true,

      text: null,
      // 필수요소
      email: null,
      modelIndex: null,
      broadcastToken: null, // 미리보기 BroadcastBox의 virtupet sub 자격증명(ensureToken)
      petId: null, // 선택 펫 id (initLiveRemote에서 세팅) — 로컬 디버그 표시용
      isLocalEnv: isLocal(), // 로컬 환경에서만 pet_id 노출
      //
      liveModelCount: 0,
      liveModelBreakdown: {}, // 경로별 연결 분포 { "/on-air": 1, "/on-air/dev": 2, ... }
      // instructions,

      // ModelFacade
      context: {
        streamerAliasId: null,
        virtupetProfile: {},
        donation: {
          profile: {
            nickname: "만수르",
          },
          extras: {
            payAmount: 5000,
          },
        },
      },

      //
    };
  },
  computed: {
    // OBS 배지 숫자 = 실제 송출 오버레이(/on-air) 개수만. 게이트웨이 끊기면 "-".
    onAirCount() {
      if (this.liveModelCount === "-") return "-";
      return this.liveModelBreakdown["/on-air"] || 0;
    },
  },
  methods: {
    // USER INTERACTION
    onClickTest() {
      // TODO: REFACTOR
      const instructionId = "introduce-type2";
      // 로컬 echo 제거: 발행 → 게이트웨이 fan-out → 미리보기 sub가 받아 반응(미러)
      this.requestExecuteInstruction(instructionId, this.context);
    },
    onClickPlayActionOnly /* not include request */(instructionId) {
      this.executeInstruction(instructionId);
    },

    // 통신 채널
    async initLiveRemote() {
      try {
        // 제어 라우팅 키 = 선택 펫 id (email 아님). 펫 단위 pub 인스턴스(컨트롤러들과 공유).
        this.petId = await SupabaseApi.UserStatus.fetchSelectedPetId();

        const socket = getVirtupetPubSocket(this.petId).socket; // init(petId)은 인스턴스가 이미 수행
        this._socket = socket;

        socket.on("notifyMessage", (data) => {
          const { name } = data;
          if (name === "NEW_LIVE_MODEL_CONNECTED") {
            if (this.$refs.obsLinkDialog) this.$refs.obsLinkDialog.onClose();
            this.liveModelCount = data.detail.liveModelCount;
            this.liveModelBreakdown = data.detail.breakdown || {};
          } else if (name === "LIVE_MODEL_DISCONNECTED") {
            this.liveModelCount = data.detail.liveModelCount;
            this.liveModelBreakdown = data.detail.breakdown || {};
          }
        });

        socket.on("commandResult", (data) => {
          this.liveModelCount = data.detail.liveModelCount;
          if (data.detail.breakdown) this.liveModelBreakdown = data.detail.breakdown;
        });

        socket.on("request", (data) => {
          console.log("[LiveRemotePage]", data);
        });

        socket.on("connect", () => {
          // TODO: 연결되었을때 현재 live model connected 된 상태를 가져와야 됨
          // 여기는 모델이 먼저 있고, 리모컨이 먼저 연결되는 경우고
          // 다른쪽은 내가 먼저 있고 모델이 늦게 연결되는 경우
        });

        socket.on("disconnect", () => {
          this.liveModelCount = "-";
          this.liveModelBreakdown = {};
        });
      } catch (error) {
        console.error(error);
      }
    },

    // REQUEST TYPE
    async requestExecuteInstruction(actionId, context) {
      if (!actionId) return;

      // 발행 DTO 자체가 StreamEvent(V-INSTRUCTION) — 오버레이 단일 인터프리터가 type으로 바로 실행.
      // (broker는 payload 무관하게 같은 petId /sub 구독자에 fan-out)
      this._socket.emit("requestCommand", {
        type: "V-INSTRUCTION",
        to: "liveModel",
        actionId, // instructionId
        context,
      });

      this.$refs.modelSyncTrigger.trigger();
    },

    updateOuterVisibility(outerFacadeVisibility) {
      // TheLeftController.created()의 초기 emit이 broadcastBox 마운트 전에 올 수 있어 가드.
      if (this.$refs.broadcastBox) this.$refs.broadcastBox.initOuterVisibility(outerFacadeVisibility);
    },

    executeInstruction(...args) {
      this.$refs.broadcastBox.executeInstruction(...args);
    },

    //
    getContext() {
      return {
        streamerAliasId: this.streamerAliasId,
        ...this.context,
      };
    },
  },
};
</script>

<style lang="scss" scoped>
/* 플랫폼 연결 섹션 카드 — 방송하기와 동일 폭, 그림자 없음(인라인 box-shadow:none) */
.platform-section {
  width: 782px;
}

/* 래퍼 통합 후: 카드 자신에게 직접 적용. 화면 크기 무관 782px 고정 */
.layout-2-1 {
  position: sticky;
  top: 10px;

  width: 782px;
  height: fit-content;
}

.app-page__content {
  border: 1px solid grey;
  border-radius: 10px;
  /* background-color: #9155fd; */
}

.app-page__content.active {
  border: 1px solid #9155fd;
  box-shadow: #9155fd 0px 5px 15px;
  /* animation: bounce 2.5s ease infinite; */
}

>>> .v-expansion-panel-content__wrap {
  padding-top: 0;
}

@keyframes bounce {
  0%,
  100% {
    box-shadow: #9155fd 0px 5px 15px;
  }
  50% {
    box-shadow: #9155fd 0px 5px 25px;
  }
}

.fade-out {
  transition: opacity 0.5s ease-out;
  opacity: 0;
}
</style>
